<template>
  <Teleport to="body">
    <div v-if="visible" class="image-verification-overlay" :class="{ success, fail }">
      <div class="verification-modal">
        <div class="verification-header">
          <div class="header-title">
            <span class="title-main">安全验证</span>
          </div>
          <div class="header-info">
            <span class="timer-info">⏱️ <span :class="{ warning: remaining <= 10 }">{{ remaining }}s</span></span>
            <button class="close-btn" @click="handleClose">✕</button>
          </div>
        </div>

        <div v-if="currentMode === 'slider'" class="verification-content">
          <div class="verification-instruction">
            <span class="instruction-label">拖动下方滑块完成拼图</span>
          </div>

          <div class="verification-image-container">
            <div class="image-wrapper">
              <div class="background-image" :style="{ background: currentBackgroundImage }"></div>
              
              <!-- 拼图块 -->
              <div class="puzzle-block" :style="{ 
                left: blockPosition + 'px', 
                top: targetY + 'px'
              }" :class="{ success: isSuccess }">
              </div>
              
              <!-- 缺口 -->
              <div class="puzzle-gap" :style="{ 
                left: targetX + 'px', 
                top: targetY + 'px'
              }" :class="{ success: isSuccess }">
              </div>
            </div>
            
            <div class="ai-generated-label">AI生成背景</div>
          </div>

          <div class="slider-container">
            <div class="slider-track">
              <div class="slider-fill" :style="{ width: sliderPosition + 'px' }"></div>
              <div
                class="slider-thumb"
                :class="{ dragging: isDragging, success: isSuccess }"
                :style="{ left: sliderPosition + 'px' }"
                @mousedown="startDrag"
                @touchstart="startDrag"
              >
                <span class="slider-icon">≡≡≡</span>
              </div>
            </div>
            <span class="slider-hint">{{ isSuccess ? '验证成功' : '向右拖动滑块' }}</span>
          </div>
        </div>

        <div v-else-if="currentMode === 'icon'" class="verification-content">
          <div class="verification-instruction">
            <span class="instruction-label">请依次点击：</span>
            <div class="target-icons">
              <span v-for="(icon, index) in targetIcons" :key="index" class="target-icon">{{ icon }}</span>
            </div>
          </div>

          <div class="verification-image-container">
            <div class="image-wrapper">
              <div class="background-image" :style="{ background: currentBackgroundImage }"></div>
              
              <div class="clickable-items">
                <div
                  v-for="(item, index) in clickableIcons"
                  :key="index"
                  class="clickable-item"
                  :class="{ 
                    selected: selectedOrder.includes(index),
                    correct: selectedOrder.includes(index) && clickableIcons[index].correctIndex === selectedOrder.indexOf(index)
                  }"
                  :style="{ top: item.top + '%', left: item.left + '%' }"
                  @click="selectIcon(index)"
                >
                  <span class="item-icon">{{ item.icon }}</span>
                  <span v-if="selectedOrder.includes(index)" class="item-order">{{ selectedOrder.indexOf(index) + 1 }}</span>
                </div>
              </div>
            </div>
            
            <div class="ai-generated-label">AI生成背景</div>
          </div>
        </div>

        <div v-else-if="currentMode === 'text'" class="verification-content">
          <div class="verification-instruction">
            <span class="instruction-label">请依次点击：</span>
            <span class="target-text">{{ targetTextDisplay }}</span>
          </div>

          <div class="verification-image-container">
            <div class="image-wrapper text-mode">
              <div class="background-image" :style="{ background: currentBackgroundImage }"></div>
              
              <div class="clickable-items">
                <div
                  v-for="(item, index) in clickableText"
                  :key="index"
                  class="clickable-text-item"
                  :class="{ 
                    selected: selectedOrder.includes(index),
                    correct: selectedOrder.includes(index) && clickableText[index].correctIndex === selectedOrder.indexOf(index)
                  }"
                  :style="{ top: item.top + '%', left: item.left + '%' }"
                  @click="selectText(index)"
                >
                  <span class="text-item-content">{{ item.text }}</span>
                  <span v-if="selectedOrder.includes(index)" class="text-item-order">{{ selectedOrder.indexOf(index) + 1 }}</span>
                </div>
              </div>
            </div>
            
            <div class="ai-generated-label">AI生成背景</div>
          </div>
        </div>

        <div class="verification-footer">
          <div class="footer-actions">
            <button class="action-btn help-btn" @click="showHelp">❓</button>
            <button class="action-btn refresh-btn" @click="refresh">🔄</button>
          </div>
          <button v-if="currentMode !== 'slider'" class="confirm-btn" @click="validate" :disabled="!canSubmit">确定</button>
        </div>

        <Transition name="result">
          <div v-if="showResult" class="verification-result" :class="resultType ? resultType + '-result' : ''">
            <div class="result-icon">{{ resultIcon }}</div>
            <div class="result-text">{{ resultText }}</div>
          </div>
        </Transition>

        <Transition name="warning">
          <div v-if="showWarning" class="verification-warning">
            <span class="warning-icon">⚠️</span>
            <span class="warning-text">{{ warningText }}</span>
          </div>
        </Transition>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

interface Props {
  modelValue: boolean
  mode?: 'slider' | 'icon' | 'text' | 'random'
  maxAttempts?: number
  timeout?: number
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'random',
  maxAttempts: 5,
  timeout: 60000
})

const emit = defineEmits(['update:modelValue', 'success', 'fail'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const backgroundImages = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
]

const iconList = ['📁', '🖼️', '🎵', '🎬', '📄', '⬇️', '⚙️', '🔍', '👤', '📧', '📞', '📍', '🛒', '❤️', '😊', '🌙', '☀️', '⭐']

const textList = ['澈', '骋', '半', '斗', '马', '牛', '羊', '鸟', '山', '水', '云', '风', '雨', '雪', '雷', '电', '日', '月', '星', '花', '草', '树', '木', '石']

// 拼图验证
const currentBackgroundImage = ref('')
const currentMode = ref<'slider' | 'icon' | 'text'>('slider')
const targetX = ref(150)
const targetY = ref(60)
const blockPosition = ref(0)
const sliderPosition = ref(0)
const isDragging = ref(false)
const isSuccess = ref(false)

// 图标点击验证
const targetIcons = ref<string[]>([])
const clickableIcons = ref<any[]>([])
const selectedOrder = ref<number[]>([])

// 文字点击验证
const targetTextDisplay = ref('')
const targetTextList = ref<string[]>([])
const clickableText = ref<any[]>([])

const attempts = ref(0)
const remaining = ref(60)
const showResult = ref(false)
const resultType = ref<'success' | 'error' | null>(null)
const resultIcon = ref('')
const resultText = ref('')
const showWarning = ref(false)
const warningText = ref('')

let startTime = 0
let timerInterval: ReturnType<typeof setInterval> | null = null
let lastClickTime = 0

const canSubmit = computed(() => {
  if (currentMode.value === 'icon' || currentMode.value === 'text') {
    return selectedOrder.value.length === (currentMode.value === 'icon' ? targetIcons.value.length : targetTextList.value.length)
  }
  return false
})

const getRandomMode = (): 'slider' | 'icon' | 'text' => {
  const modes: ('slider' | 'icon' | 'text')[] = ['slider', 'icon', 'text']
  return modes[Math.floor(Math.random() * modes.length)]
}

const generatePuzzle = () => {
  targetX.value = Math.floor(Math.random() * 200) + 80
  targetY.value = Math.floor(Math.random() * 100) + 40
  blockPosition.value = 0
  sliderPosition.value = 0
  isSuccess.value = false
}

const generateIconTargets = () => {
  const targetCount = 3
  const extraCount = 1 + Math.floor(Math.random() * 2)
  const shuffledIcons = [...iconList].sort(() => Math.random() - 0.5)
  targetIcons.value = shuffledIcons.slice(0, targetCount)
  
  const usedPositions: { top: number; left: number }[] = []
  clickableIcons.value = []
  
  const isPositionValid = (newTop: number, newLeft: number, minDistance: number = 18): boolean => {
    for (const pos of usedPositions) {
      const distance = Math.sqrt(Math.pow(newTop - pos.top, 2) + Math.pow(newLeft - pos.left, 2))
      if (distance < minDistance) {
        return false
      }
    }
    return true
  }
  
  for (let i = 0; i < targetIcons.value.length; i++) {
    let top = 0
    let left = 0
    let att = 0
    do {
      top = Math.random() * 55 + 12
      left = Math.random() * 65 + 12
      att++
    } while (!isPositionValid(top, left) && att < 100)
    usedPositions.push({ top, left })
    
    clickableIcons.value.push({
      icon: targetIcons.value[i],
      top,
      left,
      correctIndex: i
    })
  }
  
  const extraIcons = shuffledIcons.slice(targetCount, targetCount + extraCount)
  extraIcons.forEach(icon => {
    let top = 0
    let left = 0
    let att = 0
    do {
      top = Math.random() * 55 + 12
      left = Math.random() * 65 + 12
      att++
    } while (!isPositionValid(top, left) && att < 100)
    usedPositions.push({ top, left })
    
    clickableIcons.value.push({
      icon,
      top,
      left,
      correctIndex: -1
    })
  })
  
  clickableIcons.value.sort(() => Math.random() - 0.5)
  selectedOrder.value = []
}

const generateTextTargets = () => {
  const targetCount = 3
  const extraCount = 1 + Math.floor(Math.random() * 2)
  const shuffledText = [...textList].sort(() => Math.random() - 0.5)
  targetTextList.value = shuffledText.slice(0, targetCount)
  targetTextDisplay.value = targetTextList.value.join(' ')
  
  const usedPositions: { top: number; left: number }[] = []
  clickableText.value = []
  
  const minDistance = 80
  
  const isPositionValid = (newTop: number, newLeft: number): boolean => {
    for (const pos of usedPositions) {
      const distance = Math.sqrt(Math.pow(newTop - pos.top, 2) + Math.pow(newLeft - pos.left, 2))
      if (distance < minDistance) {
        return false
      }
    }
    return true
  }
  
  const positions = [
    { top: 25, left: 20 },
    { top: 25, left: 50 },
    { top: 25, left: 80 },
    { top: 60, left: 20 },
    { top: 60, left: 50 },
    { top: 60, left: 80 },
    { top: 42, left: 35 },
    { top: 42, left: 65 }
  ]
  
  const shuffledPositions = [...positions].sort(() => Math.random() - 0.5)
  
  for (let i = 0; i < targetTextList.value.length; i++) {
    const pos = shuffledPositions[i]
    usedPositions.push(pos)
    
    clickableText.value.push({
      text: targetTextList.value[i],
      top: pos.top,
      left: pos.left,
      correctIndex: i
    })
  }
  
  const remainingPositions = shuffledPositions.slice(targetTextList.value.length, targetTextList.value.length + extraCount)
  const extraText = shuffledText.slice(targetCount, targetCount + extraCount)
  
  extraText.forEach((text, index) => {
    const pos = remainingPositions[index] || {
      top: Math.random() * 40 + 20,
      left: Math.random() * 50 + 20
    }
    usedPositions.push(pos)
    
    clickableText.value.push({
      text,
      top: pos.top,
      left: pos.left,
      correctIndex: -1
    })
  })
  
  clickableText.value.sort(() => Math.random() - 0.5)
  selectedOrder.value = []
}

const selectIcon = (index: number) => {
  if (isSuccess.value || selectedOrder.value.includes(index)) return
  
  const now = Date.now()
  if (now - lastClickTime < 300) {
    showWarningMessage('点击速度过快，请稍候再试')
    return
  }
  lastClickTime = now
  
  selectedOrder.value.push(index)
  
  if (selectedOrder.value.length === targetIcons.value.length) {
    setTimeout(() => validate(), 300)
  }
}

const selectText = (index: number) => {
  if (isSuccess.value || selectedOrder.value.includes(index)) return
  
  const now = Date.now()
  if (now - lastClickTime < 300) {
    showWarningMessage('点击速度过快，请稍候再试')
    return
  }
  lastClickTime = now
  
  selectedOrder.value.push(index)
  
  if (selectedOrder.value.length === targetTextList.value.length) {
    setTimeout(() => validate(), 300)
  }
}

const validate = () => {
  if (Date.now() - startTime > props.timeout) {
    handleFail('验证超时，请重新尝试')
    return
  }
  
  const totalTime = Date.now() - startTime
  if (totalTime < 500) {
    handleFail('验证速度异常，请重新尝试')
    return
  }
  
  if (currentMode.value === 'slider') {
    const tolerance = 5
    const isCorrect = Math.abs(blockPosition.value - targetX.value) <= tolerance
    
    if (isCorrect) {
      handleSuccess()
    } else {
      handleFail('验证失败，请重试')
    }
  } else if (currentMode.value === 'icon') {
    const isCorrect = selectedOrder.value.every((itemIndex, i) => {
      return clickableIcons.value[itemIndex].correctIndex === i
    })
    
    if (isCorrect) {
      handleSuccess()
    } else {
      handleFail('验证失败，请重试')
    }
  } else if (currentMode.value === 'text') {
    const isCorrect = selectedOrder.value.every((itemIndex, i) => {
      return clickableText.value[itemIndex].correctIndex === i
    })
    
    if (isCorrect) {
      handleSuccess()
    } else {
      handleFail('验证失败，请重试')
    }
  }
}

const handleSuccess = () => {
  isSuccess.value = true
  showResult.value = true
  resultType.value = 'success'
  resultIcon.value = '✓'
  resultText.value = '验证成功'
  
  setTimeout(() => {
    emit('success', {
      mode: currentMode.value,
      duration: Date.now() - startTime,
      attempts: attempts.value + 1
    })
    visible.value = false
    showResult.value = false
    resultType.value = null
  }, 1000)
}

const handleFail = (message: string) => {
  attempts.value++
  
  showResult.value = true
  resultType.value = 'error'
  resultIcon.value = '✗'
  resultText.value = message
  
  if (attempts.value >= props.maxAttempts) {
    setTimeout(() => {
      emit('fail', {
        reason: 'max_attempts_exceeded',
        attempts: attempts.value
      })
      visible.value = false
      showResult.value = false
      resultType.value = null
    }, 1500)
  } else {
    setTimeout(() => {
      showResult.value = false
      resultType.value = null
      refresh()
    }, 1500)
  }
}

const showWarningMessage = (message: string) => {
  warningText.value = message
  showWarning.value = true
  setTimeout(() => {
    showWarning.value = false
  }, 2000)
}

const showHelp = () => {
  const helpText = {
    slider: '请拖动滑块将拼图块移动到正确位置',
    icon: '请按照提示顺序，依次点击对应的图标',
    text: '请按照提示顺序，依次点击对应的文字'
  }
  alert(helpText[currentMode.value] || helpText.slider)
}

const startTimer = () => {
  remaining.value = Math.floor(props.timeout / 1000)
  timerInterval = setInterval(() => {
    remaining.value--
    if (remaining.value <= 0) {
      handleFail('验证超时')
    }
  }, 1000)
}

const stopTimer = () => {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

const refresh = () => {
  stopTimer()
  isSuccess.value = false
  attempts.value = 0
  startTime = Date.now()
  lastClickTime = 0
  selectedOrder.value = []
  
  currentBackgroundImage.value = backgroundImages[Math.floor(Math.random() * backgroundImages.length)]
  
  if (props.mode === 'random') {
    currentMode.value = getRandomMode()
  } else {
    currentMode.value = props.mode
  }
  
  if (currentMode.value === 'slider') {
    generatePuzzle()
  } else if (currentMode.value === 'icon') {
    generateIconTargets()
  } else if (currentMode.value === 'text') {
    generateTextTargets()
  }
  
  startTimer()
}

const handleClose = () => {
  stopTimer()
  visible.value = false
  emit('fail', { reason: 'closed_by_user' })
}

const startDrag = (e: MouseEvent | TouchEvent) => {
  if (isSuccess.value) return
  
  isDragging.value = true
  const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX
  const startX = clientX
  const startSliderPos = sliderPosition.value
  
  const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
    if (!isDragging.value) return
    const moveClientX = 'touches' in moveEvent
      ? (moveEvent as TouchEvent).touches[0].clientX
      : (moveEvent as MouseEvent).clientX
    
    const container = document.querySelector('.slider-container')
    if (!container) return
    const rect = container.getBoundingClientRect()
    let newLeft = moveClientX - startX + startSliderPos
    newLeft = Math.max(0, Math.min(newLeft, rect.width - 50))
    sliderPosition.value = newLeft
    blockPosition.value = newLeft
  }
  
  const handleEnd = () => {
    if (isDragging.value) {
      isDragging.value = false
      validate()
    }
    document.removeEventListener('mousemove', handleMove as EventListener)
    document.removeEventListener('mouseup', handleEnd)
    document.removeEventListener('touchmove', handleMove as EventListener)
    document.removeEventListener('touchend', handleEnd)
  }
  
  document.addEventListener('mousemove', handleMove as EventListener)
  document.addEventListener('mouseup', handleEnd)
  document.addEventListener('touchmove', handleMove as EventListener, { passive: false })
  document.addEventListener('touchend', handleEnd)
}

onMounted(() => {
  if (props.modelValue) {
    refresh()
  }
})

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    refresh()
  } else {
    stopTimer()
  }
})

onUnmounted(() => {
  stopTimer()
})
</script>

<style scoped>
.image-verification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
  backdrop-filter: blur(4px);
}

.image-verification-overlay.success .verification-modal {
  border-color: #4CAF50;
  box-shadow: 0 0 30px rgba(76, 175, 80, 0.5);
}

.image-verification-overlay.fail .verification-modal {
  border-color: #f44336;
  box-shadow: 0 0 30px rgba(244, 67, 54, 0.5);
  animation: shake 0.5s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-15px); }
  40% { transform: translateX(15px); }
  60% { transform: translateX(-10px); }
  80% { transform: translateX(10px); }
}

.verification-modal {
  background: white;
  border-radius: 16px;
  padding: 20px;
  width: 95%;
  max-width: 380px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 4px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.verification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.header-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title-main {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #666;
}

.timer-info {
  color: #4CAF50;
  font-weight: 500;
}

.timer-info.warning {
  color: #f44336;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  color: #999;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #e0e0e0;
  color: #666;
}

.verification-instruction {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.instruction-label {
  color: #333;
  font-weight: 500;
}

.target-icons {
  display: flex;
  gap: 8px;
}

.target-icon {
  font-size: 20px;
}

.target-text {
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 4px;
  color: #333;
}

.verification-image-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.image-wrapper {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 8px;
}

.background-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.puzzle-block {
  position: absolute;
  width: 50px;
  height: 50px;
  background-size: 380px 200px;
  background-repeat: no-repeat;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3);
  transition: left 0.05s ease-out, box-shadow 0.2s ease;
}

.puzzle-block.success {
  box-shadow: 0 4px 20px rgba(76, 175, 80, 0.7), 0 0 0 2px rgba(76, 175, 80, 0.5);
}

.puzzle-gap {
  position: absolute;
  width: 50px;
  height: 50px;
  background: rgba(0, 0, 0, 0.45);
  border: 2px dashed rgba(255, 255, 255, 0.6);
  border-radius: 4px;
  box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 0, 0, 0.2);
}

.puzzle-gap.success {
  background: rgba(76, 175, 80, 0.35);
  border-color: #4CAF50;
  box-shadow: inset 0 0 10px rgba(76, 175, 80, 0.3), 0 0 0 1px rgba(76, 175, 80, 0.2);
}

.ai-generated-label {
  position: absolute;
  bottom: 8px;
  right: 10px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.clickable-items {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.clickable-item {
  position: absolute;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  transform: translate(-50%, -50%);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  border: 2px solid transparent;
  backdrop-filter: blur(4px);
}

.clickable-item:hover {
  transform: translate(-50%, -50%) scale(1.18);
  background: white;
  border-color: #1890ff;
  box-shadow: 0 6px 20px rgba(24, 144, 255, 0.3), 0 0 0 2px rgba(24, 144, 255, 0.1);
}

.clickable-item.selected {
  background: rgba(24, 144, 255, 0.12);
  border-color: #1890ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
}

.clickable-item.correct {
  background: rgba(76, 175, 80, 0.15);
  border-color: #4CAF50;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.25);
}

.clickable-item.text-item {
  width: 50px;
  height: 50px;
  font-size: 24px;
  font-weight: 700;
  color: #333;
  background: rgba(255, 255, 255, 0.98);
  border: 3px solid rgba(0, 0, 0, 0.15);
}

.item-icon,
.item-text {
  position: relative;
  z-index: 1;
}

.item-order {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: #1890ff;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  z-index: 2;
}

.slider-container {
  padding: 8px 0;
}

.slider-track {
  position: relative;
  width: 100%;
  height: 44px;
  background: #f5f5f5;
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
}

.slider-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #1890ff 0%, #40a9ff 50%, #69c0ff 100%);
  transition: width 0.05s ease-out;
}

.slider-thumb {
  position: absolute;
  left: 0;
  top: 0;
  width: 44px;
  height: 44px;
  background: linear-gradient(145deg, #ffffff, #f0f0f0);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  user-select: none;
  font-size: 14px;
  color: #1890ff;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slider-thumb.dragging {
  cursor: grabbing;
  transform: scale(1.08);
  box-shadow: 0 6px 16px rgba(24, 144, 255, 0.3), 0 0 0 2px rgba(24, 144, 255, 0.1);
}

.slider-thumb.success {
  background: linear-gradient(145deg, #4CAF50, #45a049);
  color: white;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3), 0 0 0 1px rgba(76, 175, 80, 0.1);
}

.slider-icon {
  letter-spacing: 1px;
}

.slider-hint {
  display: block;
  text-align: center;
  margin-top: 10px;
  color: #666;
  font-size: 13px;
}

.verification-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.footer-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(145deg, #f8f9fa, #e9ecef);
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.03);
}

.action-btn:hover {
  background: linear-gradient(145deg, #e9ecef, #dee2e6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.confirm-btn {
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 50%, #69c0ff 100%);
  color: white;
  padding: 10px 28px;
  font-weight: 500;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
}

.confirm-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(24, 144, 255, 0.35);
}

.confirm-btn:disabled {
  background: linear-gradient(135deg, #d9d9d9, #e0e0e0);
  cursor: not-allowed;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.verification-result {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  animation: fadeIn 0.3s ease;
  z-index: 10;
}

.success-result {
  background: rgba(76, 175, 80, 0.95);
}

.error-result {
  background: rgba(244, 67, 54, 0.95);
}

.result-icon {
  font-size: 56px;
  color: white;
  margin-bottom: 12px;
}

.result-text {
  font-size: 20px;
  font-weight: bold;
  color: white;
}

.verification-warning {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 152, 0, 0.95);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: slideDown 0.3s ease;
  z-index: 20;
}

@keyframes slideDown {
  from {
    transform: translate(-50%, -70%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%);
    opacity: 1;
  }
}

.warning-icon {
  font-size: 18px;
}

.warning-text {
  font-size: 13px;
  font-weight: 500;
}

.image-wrapper.text-mode {
  position: relative;
}

.clickable-text-item {
  position: absolute;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transform: translate(-50%, -50%);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
}

.clickable-text-item:hover {
  transform: translate(-50%, -50%) scale(1.2);
}

.clickable-text-item.selected {
  transform: translate(-50%, -50%) scale(1.15);
}

.clickable-text-item.correct {
  transform: translate(-50%, -50%) scale(1.15);
}

.text-item-content {
  font-size: 32px;
  font-weight: 800;
  color: #FFD700;
  text-shadow: 
    2px 2px 0 #333,
    -1px -1px 0 #333,
    1px -1px 0 #333,
    -1px 1px 0 #333,
    2px 2px 4px rgba(0, 0, 0, 0.8);
  letter-spacing: 2px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.clickable-text-item:hover .text-item-content {
  color: #FFEC8B;
  text-shadow: 
    2px 2px 0 #333,
    -1px -1px 0 #333,
    1px -1px 0 #333,
    -1px 1px 0 #333,
    2px 2px 8px rgba(0, 0, 0, 0.9);
}

.clickable-text-item.selected .text-item-content {
  color: #1890ff;
  text-shadow: 
    2px 2px 0 #333,
    -1px -1px 0 #333,
    1px -1px 0 #333,
    -1px 1px 0 #333,
    2px 2px 8px rgba(24, 144, 255, 0.8);
}

.clickable-text-item.correct .text-item-content {
  color: #4CAF50;
  text-shadow: 
    2px 2px 0 #333,
    -1px -1px 0 #333,
    1px -1px 0 #333,
    -1px 1px 0 #333,
    2px 2px 8px rgba(76, 175, 80, 0.8);
}

.text-item-order {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 28px;
  height: 28px;
  background: #1890ff;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  z-index: 20;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.5);
}
</style>
